import { useState, useEffect, useCallback, useRef } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.jsx'
import Nango from '@nangohq/frontend'
import ConnectorCatalogModal from './ConnectorCatalogModal.jsx'
import ConnectionModal from './ConnectionModal.jsx'
import ReauthCredentialsModal from './ReauthCredentialsModal.jsx'
import BrandLogo from './BrandLogo.jsx'
import { CATALOG_BY_ID } from './connectorCatalog.js'
import styles from './ConnectorsPage.module.css'

// ── PII / PCI signal detection ────────────────────────────────────────────────
// Column name tokens that indicate high-risk fields. Matched as word-boundary
// substrings so "customer_ssn" hits "ssn" but "company" does not hit "pan".
const _PII_PCI_SIGNALS = [
  // Government / identity
  'ssn', 'social_security', 'social_security_number', 'tax_id', 'sin',
  'national_id', 'national_insurance', 'passport', 'passport_number',
  'driver_license', 'drivers_license',
  // Date of birth
  'date_of_birth', 'dob', 'birth_date', 'birthdate',
  // Payment card (PCI DSS)
  'card_number', 'credit_card', 'debit_card', 'pan', 'card_no',
  'cc_number', 'ccnum', 'cvv', 'cvc', 'cvv2', 'cvc2',
  'security_code', 'card_security_code', 'card_expiry', 'expiry_date',
  'expiration_date', 'track_data', 'track1', 'track2', 'magnetic_stripe',
  // Banking
  'account_number', 'routing_number', 'iban', 'bank_account',
  // Medical / biometric
  'medical_record', 'diagnosis', 'health_record', 'biometric', 'fingerprint',
]

function _detectPiiColumns(tables) {
  // Returns [{ table, column, signal }] for every flagged column across all tables
  const results = []
  for (const t of tables) {
    for (const col of t.columns || []) {
      const lower = col.toLowerCase()
      const hit = _PII_PCI_SIGNALS.find(sig => {
        const re = new RegExp(`(?<![a-z0-9])${sig}(?![a-z0-9])`)
        return re.test(lower)
      })
      if (hit) results.push({ table: t.name, column: col, signal: hit })
    }
  }
  return results
}

const SCHEDULE_OPTIONS = [
  { id: 'every_15m',      label: 'Every 15 minutes',   cron: '*/15 * * * *',  hours: 0.25 },
  { id: 'hourly',         label: 'Hourly',              cron: '0 * * * *',     hours: 1    },
  { id: 'every_4h',       label: 'Every 4 hours',       cron: '0 */4 * * *',   hours: 4    },
  { id: 'every_6h',       label: 'Every 6 hours',       cron: '0 */6 * * *',   hours: 6    },
  { id: 'every_12h',      label: 'Every 12 hours',      cron: '0 */12 * * *',  hours: 12   },
  { id: 'daily_midnight', label: 'Daily at midnight',   cron: '0 0 * * *',     hours: 24   },
  { id: 'off',            label: 'Off — manual only',   cron: null,            hours: null  },
]

const CRON_TO_ID = Object.fromEntries(SCHEDULE_OPTIONS.map(s => [s.cron, s.id]))

function nextSyncLabel(schedId) {
  if (schedId === 'daily_midnight') {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(0, 0, 0, 0)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  const hours = SCHEDULE_OPTIONS.find(s => s.id === schedId)?.hours ?? 6
  return new Date(Date.now() + hours * 3_600_000)
    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function lastSyncAgoLabel(isoString) {
  if (!isoString) return null
  if (!isoString.match(/^\d{4}-/)) return isoString
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function SyncScheduleBar({ connectorId, schedule, lastSync, apiFetch, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving]   = useState(false)
  const [draft, setDraft]     = useState(schedule)
  const opt = SCHEDULE_OPTIONS.find(s => s.id === schedule)

  const save = async () => {
    setSaving(true)
    const opt  = SCHEDULE_OPTIONS.find(s => s.id === draft)
    const cron = opt?.cron ?? null
    try {
      await apiFetch(`/api/connectors/${connectorId}`, {
        method: 'PATCH',
        body: JSON.stringify({ sync_schedule: cron ?? '' }),
      })
      onSaved(draft, cron)
    } catch (err) {
      console.error('Failed to save sync schedule:', err)
    }
    setSaving(false)
    setEditing(false)
  }

  const lastAgo = lastSyncAgoLabel(lastSync)
  const isOff   = schedule === 'off' || !schedule

  return (
    <div className={styles.scheduleBar}>
      {!editing ? (
        <>
          <span className={styles.scheduleIcon}>⏱</span>
          <span className={styles.scheduleText}>
            Syncs: <strong>{opt?.label ?? schedule ?? 'Off — manual only'}</strong>
          </span>
          {lastAgo && (
            <>
              <span className={styles.scheduleSep}>·</span>
              <span className={styles.scheduleNext}>Last {lastAgo}</span>
            </>
          )}
          {!isOff && (
            <>
              <span className={styles.scheduleSep}>·</span>
              <span className={styles.scheduleNext}>Next ~{nextSyncLabel(schedule)}</span>
            </>
          )}
          <button className={styles.scheduleEditBtn} onClick={() => setEditing(true)}>Edit schedule</button>
        </>
      ) : (
        <div className={styles.scheduleEditPanel}>
          <div className={styles.scheduleEditLabel}>Sync frequency</div>
          <div className={styles.scheduleOptions}>
            {SCHEDULE_OPTIONS.map(opt => (
              <label key={opt.id} className={`${styles.scheduleOption} ${draft === opt.id ? styles.scheduleOptionActive : ''}`}>
                <input type="radio" name="sync_schedule" value={opt.id} checked={draft === opt.id} onChange={() => setDraft(opt.id)} />
                <span>{opt.label}</span>
                <span className={styles.scheduleCron}>{opt.cron}</span>
              </label>
            ))}
          </div>
          <div className={styles.scheduleActions}>
            <button className={styles.scheduleSaveBtn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className={styles.scheduleCancelBtn} onClick={() => { setDraft(schedule); setEditing(false) }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

const CATALOG_META = {
  quickbooks:          { name: 'QuickBooks Online',  category: 'Accounting',     icon: '₿' },
  'quickbooks-sandbox': { name: 'QuickBooks Sandbox', category: 'Accounting',     icon: '₿' },
  xero:       { name: 'Xero',                  category: 'Accounting',     icon: '✕' },
  gusto:      { name: 'Gusto',                 category: 'HR / Payroll',   icon: '◉' },
  shopify:    { name: 'Shopify',               category: 'Sales / Orders', icon: '◧' },
  salesforce: { name: 'Salesforce',            category: 'CRM',            icon: '⌁' },
  hubspot:    { name: 'HubSpot',               category: 'CRM',            icon: '◆' },
  stripe:     { name: 'Stripe',                category: 'Payments',       icon: '▷' },
  'stripe-sandbox': { name: 'Stripe Sandbox',  category: 'Payments',       icon: '▷' },
  'erp-rest': { name: 'Custom REST API',       category: 'Custom',         icon: '⚙' },
  netsuite:   { name: 'NetSuite',              category: 'ERP',            icon: '▦' },
  sap:        { name: 'SAP',                   category: 'ERP',            icon: '◐' },
  dynamics:   { name: 'Microsoft Dynamics',    category: 'ERP',            icon: '▣' },
  adp:        { name: 'ADP Workforce',         category: 'HR / Payroll',   icon: '◈' },
  rippling:   { name: 'Rippling',              category: 'HR / Payroll',   icon: '◇' },
  square:     { name: 'Square POS',            category: 'Sales / Orders', icon: '▪' },
  zendesk:    { name: 'Zendesk',               category: 'Support',        icon: '◎' },
  servicenow: { name: 'ServiceNow',            category: 'Support',        icon: '◬' },
}

const PROVIDER_SCHEMAS = {
  quickbooks: {
    description: 'Accounting — invoices, customers, vendors, payments, and chart of accounts',
    tables: [
      { name: 'invoices',   rows: 14820, columns: ['Id', 'DocNumber', 'TxnDate', 'TotalAmt', 'CustomerRef', 'Balance', 'Status'] },
      { name: 'customers',  rows: 3241,  columns: ['Id', 'DisplayName', 'Email', 'Balance', 'Active'] },
      { name: 'accounts',   rows: 187,   columns: ['Id', 'Name', 'AccountType', 'CurrentBalance', 'Active'] },
      { name: 'payments',   rows: 9654,  columns: ['Id', 'TxnDate', 'TotalAmt', 'CustomerRef', 'UnappliedAmt'] },
      { name: 'vendors',    rows: 412,   columns: ['Id', 'DisplayName', 'Email', 'Balance', 'Active'] },
      { name: 'items',      rows: 892,   columns: ['Id', 'Name', 'Type', 'UnitPrice', 'QtyOnHand'] },
    ],
    aiPrompts: [
      "What's my total revenue this month from QuickBooks?",
      'Which customers have outstanding balances over $10,000?',
      'Build a revenue mart from my QuickBooks data',
    ],
  },
  'quickbooks-sandbox': {
    description: 'Accounting (sandbox) — invoices, customers, vendors, payments, and chart of accounts',
    tables: [
      { name: 'invoices',   rows: null, columns: ['Id', 'DocNumber', 'TxnDate', 'TotalAmt', 'CustomerRef', 'Balance'] },
      { name: 'customers',  rows: null, columns: ['Id', 'DisplayName', 'PrimaryEmailAddr', 'Balance', 'Active'] },
      { name: 'accounts',   rows: null, columns: ['Id', 'Name', 'AccountType', 'CurrentBalance', 'Active'] },
      { name: 'bills',      rows: null, columns: ['Id', 'DocNumber', 'TxnDate', 'TotalAmt', 'VendorRef', 'Balance'] },
      { name: 'vendors',    rows: null, columns: ['Id', 'DisplayName', 'PrimaryEmailAddr', 'Balance', 'Active'] },
      { name: 'items',      rows: null, columns: ['Id', 'Name', 'Type', 'UnitPrice', 'QtyOnHand', 'Active'] },
    ],
    aiPrompts: [
      'What invoices came in from QuickBooks sandbox?',
      'Show me a sample of the sandbox customers table',
      'What tables synced from QuickBooks sandbox?',
    ],
  },
  salesforce: {
    description: 'CRM — accounts, contacts, opportunities, leads, and support cases',
    tables: [
      { name: 'accounts',      rows: 4821,  columns: ['Id', 'Name', 'Industry', 'AnnualRevenue', 'BillingCity', 'LastModifiedDate'] },
      { name: 'contacts',      rows: 12340, columns: ['Id', 'FirstName', 'LastName', 'Email', 'Phone', 'AccountId'] },
      { name: 'opportunities', rows: 8932,  columns: ['Id', 'Name', 'StageName', 'Amount', 'CloseDate', 'AccountId'] },
      { name: 'leads',         rows: 15230, columns: ['Id', 'FirstName', 'LastName', 'Email', 'Company', 'Status'] },
      { name: 'cases',         rows: 3421,  columns: ['Id', 'Subject', 'Status', 'Priority', 'AccountId'] },
    ],
    aiPrompts: [
      "What's the total value of my open pipeline right now?",
      'Which opportunities are closing this quarter?',
      'Build a pipeline mart from my Salesforce data',
    ],
  },
  hubspot: {
    description: 'CRM — contacts, companies, deals, and support tickets',
    tables: [
      { name: 'contacts',  rows: 18420, columns: ['id', 'email', 'firstname', 'lastname', 'phone', 'company', 'lastmodifieddate'] },
      { name: 'companies', rows: 5340,  columns: ['id', 'name', 'domain', 'industry', 'annualrevenue', 'city'] },
      { name: 'deals',     rows: 9871,  columns: ['id', 'dealname', 'dealstage', 'amount', 'closedate', 'pipeline'] },
      { name: 'tickets',   rows: 2103,  columns: ['id', 'subject', 'hs_ticket_priority', 'hs_pipeline_stage', 'createdate'] },
    ],
    aiPrompts: [
      'What deals moved stages in the last 7 days?',
      "What's my average deal size by pipeline stage?",
      'Build a marketing funnel mart from HubSpot data',
    ],
  },
  stripe: {
    description: 'Payments — customers, charges, invoices, subscriptions, and products',
    tables: [
      { name: 'customers',     rows: 8234,  columns: ['id', 'email', 'name', 'created', 'balance', 'currency'] },
      { name: 'charges',       rows: 31820, columns: ['id', 'amount', 'currency', 'status', 'customer', 'created'] },
      { name: 'invoices',      rows: 14392, columns: ['id', 'customer', 'amount_due', 'amount_paid', 'status', 'created'] },
      { name: 'subscriptions', rows: 3821,  columns: ['id', 'customer', 'status', 'current_period_end', 'plan_id'] },
      { name: 'products',      rows: 124,   columns: ['id', 'name', 'active', 'created', 'type'] },
    ],
    aiPrompts: [
      "What's my monthly recurring revenue from Stripe?",
      'How many subscriptions churned last month?',
      'Build a revenue recognition mart from Stripe data',
    ],
  },
  'stripe-sandbox': {
    description: 'Payments (sandbox) — customers, charges, invoices, subscriptions, and products',
    tables: [
      { name: 'customers',     rows: null, columns: ['id', 'email', 'name', 'created', 'balance', 'currency'] },
      { name: 'charges',       rows: null, columns: ['id', 'amount', 'currency', 'status', 'customer', 'created'] },
      { name: 'invoices',      rows: null, columns: ['id', 'customer', 'amount_due', 'amount_paid', 'status', 'created'] },
      { name: 'subscriptions', rows: null, columns: ['id', 'customer', 'status', 'current_period_end', 'plan_id'] },
      { name: 'products',      rows: null, columns: ['id', 'name', 'active', 'created', 'type'] },
    ],
    aiPrompts: [
      'What test customers are in my Stripe sandbox?',
      'Show me the sandbox charges table',
      'What tables synced from Stripe sandbox?',
    ],
  },
  shopify: {
    description: 'eCommerce — orders, products, customers, and inventory levels',
    tables: [
      { name: 'orders',    rows: 52340, columns: ['id', 'name', 'email', 'total_price', 'financial_status', 'created_at', 'fulfillment_status'] },
      { name: 'products',  rows: 1840,  columns: ['id', 'title', 'vendor', 'product_type', 'status', 'created_at'] },
      { name: 'customers', rows: 28910, columns: ['id', 'email', 'first_name', 'last_name', 'orders_count', 'total_spent'] },
      { name: 'inventory', rows: 3420,  columns: ['inventory_item_id', 'location_id', 'available', 'updated_at'] },
    ],
    aiPrompts: [
      "What's my best-selling product this month?",
      'Which customers have ordered more than 5 times?',
      'Build an eCommerce performance mart from Shopify',
    ],
  },
  xero: {
    description: 'Accounting — invoices, contacts, accounts, payments, and bank transactions',
    tables: [
      { name: 'invoices',          rows: 8420,  columns: ['InvoiceID', 'Type', 'Status', 'Total', 'AmountDue', 'DueDate', 'Contact'] },
      { name: 'contacts',          rows: 2340,  columns: ['ContactID', 'Name', 'EmailAddress', 'IsSupplier', 'IsCustomer'] },
      { name: 'accounts',          rows: 203,   columns: ['AccountID', 'Name', 'Type', 'Status', 'CurrentBalance'] },
      { name: 'payments',          rows: 6821,  columns: ['PaymentID', 'Date', 'Amount', 'Reference', 'Invoice'] },
      { name: 'bank_transactions', rows: 14230, columns: ['BankTransactionID', 'Date', 'Total', 'Reference', 'BankAccount'] },
    ],
    aiPrompts: [
      'What invoices are past due this week?',
      "What's my cash position across all bank accounts?",
      'Build a cash flow mart from Xero data',
    ],
  },
  gusto: {
    description: 'HR & Payroll — employees, payroll runs, and company details',
    tables: [
      { name: 'employees',  rows: 312,  columns: ['uuid', 'first_name', 'last_name', 'email', 'department', 'hire_date', 'employment_status'] },
      { name: 'payrolls',   rows: 2184, columns: ['uuid', 'pay_period_start', 'pay_period_end', 'check_date', 'totals'] },
      { name: 'companies',  rows: 1,    columns: ['uuid', 'name', 'ein', 'entity_type'] },
    ],
    aiPrompts: [
      'What was total payroll cost last month?',
      'How many employees were hired vs. departed this quarter?',
      'Build a headcount and payroll mart from Gusto data',
    ],
  },
  'sap-b1': {
    description: 'ERP — business partners, invoices, purchase orders, items, and inventory',
    tables: [
      { name: 'business_partners', rows: 2841,  columns: ['CardCode', 'CardName', 'CardType', 'Balance', 'Phone1', 'City'] },
      { name: 'invoices',          rows: 18230, columns: ['DocEntry', 'DocNum', 'DocDate', 'DocTotal', 'CardCode', 'DocStatus'] },
      { name: 'purchase_orders',   rows: 4821,  columns: ['DocEntry', 'DocNum', 'DocDate', 'DocTotal', 'CardCode', 'DocStatus'] },
      { name: 'items',             rows: 8430,  columns: ['ItemCode', 'ItemName', 'ItemType', 'OnHand', 'AvgPrice'] },
      { name: 'inventory',         rows: 3120,  columns: ['DocEntry', 'ItemCode', 'Quantity', 'WhsCode', 'DocDate'] },
    ],
    aiPrompts: [
      "What's my current inventory value by warehouse?",
      'Which purchase orders are open and past their delivery date?',
      'Build an inventory and procurement mart from SAP B1 data',
    ],
  },
  odoo: {
    description: 'ERP — partners, invoices, sales orders, purchase orders, and products',
    tables: [
      { name: 'partners',        rows: 3240,  columns: ['id', 'name', 'email', 'phone', 'is_company', 'active'] },
      { name: 'invoices',        rows: 14820, columns: ['id', 'name', 'move_type', 'state', 'invoice_date', 'amount_total', 'partner_id'] },
      { name: 'sale_orders',     rows: 6320,  columns: ['id', 'name', 'state', 'date_order', 'amount_total', 'partner_id'] },
      { name: 'purchase_orders', rows: 2841,  columns: ['id', 'name', 'state', 'date_order', 'amount_total', 'partner_id'] },
      { name: 'products',        rows: 4120,  columns: ['id', 'name', 'default_code', 'list_price', 'qty_available'] },
      { name: 'stock_moves',     rows: 31240, columns: ['id', 'name', 'state', 'product_id', 'product_qty', 'date'] },
    ],
    aiPrompts: [
      "What's my open order backlog in Odoo right now?",
      'Show me stock levels for my top 10 products',
      'Build a sales and inventory mart from Odoo data',
    ],
  },
  sharepoint: {
    description: 'Productivity — SharePoint lists and list items from your sites',
    tables: [
      { name: 'lists',           rows: 24,   columns: ['id', 'name', 'displayName', 'description', 'createdDateTime'] },
      { name: 'list_items_*',    rows: 8430, columns: ['id', '_list_id', '_list_name', 'Title', 'Modified', 'Created', 'Author'] },
    ],
    aiPrompts: [
      'What SharePoint lists came in and what data do they contain?',
      'Are there any project tracking lists I can connect to production data?',
      'Help me map my SharePoint lists to data lake tables',
    ],
  },
  paychex: {
    description: 'HR & Payroll — workers, payroll components, and pay periods',
    tables: [
      { name: 'workers',            rows: 482,  columns: ['workerId', 'givenName', 'familyName', 'emailUri', 'employmentType', 'startDate'] },
      { name: 'payroll_components', rows: 128,  columns: ['componentId', 'name', 'componentType', 'effectOnPay'] },
      { name: 'pay_periods',        rows: 312,  columns: ['payPeriodId', 'startDate', 'endDate', 'status', 'payDate'] },
    ],
    aiPrompts: [
      'What was total payroll for the last pay period?',
      'How many active workers do we have by employment type?',
      'Build a workforce cost mart from Paychex data',
    ],
  },
  mysql: {
    description: 'Database — all tables reflected from your MySQL schema',
    tables: [
      { name: '(reflected from schema)', rows: null, columns: ['All tables discovered at sync time'] },
    ],
    aiPrompts: [
      'What tables came in from my MySQL database?',
      'Show me the schema for my most important tables',
      'Help me build staging models from my MySQL source tables',
    ],
  },
  postgres: {
    description: 'Database — all tables reflected from your PostgreSQL schema',
    tables: [
      { name: '(reflected from schema)', rows: null, columns: ['All tables discovered at sync time'] },
    ],
    aiPrompts: [
      'What tables came in from my PostgreSQL database?',
      'Show me the row counts for each table',
      'Help me build staging models from my Postgres source tables',
    ],
  },
  'erp-rest': {
    description: 'Custom REST API — work orders, parts, and inventory from your internal system',
    tables: [
      { name: 'work_orders', rows: 5840,  columns: ['id', 'order_number', 'status', 'product_id', 'quantity', 'due_date', 'created_at'] },
      { name: 'parts',       rows: 18230, columns: ['id', 'part_number', 'description', 'unit_cost', 'lead_time_days'] },
      { name: 'inventory',   rows: 6012,  columns: ['id', 'part_id', 'location', 'quantity_on_hand', 'reorder_point', 'updated_at'] },
    ],
    aiPrompts: [
      'How many open work orders are past their due date?',
      'Which parts are below their reorder point?',
      'Build a production planning mart from my ERP data',
    ],
  },
}

function getSchemaForConnector(id) {
  return PROVIDER_SCHEMAS[id] ?? {
    description: 'Connected data source',
    tables: [{ name: 'records', rows: null, columns: ['id', 'created_at', 'updated_at'] }],
    aiPrompts: [`What data came in from ${id}?`, `Show me a sample from ${id}`],
  }
}

function sanitizeErrorMessage(msg) {
  if (!msg) return msg
  if (/nango\.dev|api\.nango|httpx|Client error/i.test(msg)) {
    const statusMatch = msg.match(/(\d{3})\s+\w+/);
    return statusMatch ? `Sync service error (HTTP ${statusMatch[1]})` : 'Sync service connection error'
  }
  return msg
}

function formatLastSync(raw) {
  if (!raw) return 'Never synced'
  if (typeof raw === 'string' && !raw.match(/^\d{4}-/)) return raw
  const d = new Date(raw)
  const mins = Math.floor((Date.now() - d) / 60000)
  if (mins < 1)  return 'Just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs} hr ago`
  return d.toLocaleDateString()
}

function formatRows(n) {
  if (!n && n !== 0) return null
  return Number(n).toLocaleString()
}

function computeDuration(startedAt, completedAt) {
  if (!startedAt || !completedAt) return '—'
  const ms = new Date(completedAt) - new Date(startedAt)
  if (ms < 0) return '—'
  const secs = Math.round(ms / 1000)
  if (secs < 60) return `${secs}s`
  return `${Math.floor(secs / 60)}m ${secs % 60}s`
}

function formatElapsed(ms) {
  const secs = Math.floor(ms / 1000)
  if (secs < 60) return `${secs}s`
  return `${Math.floor(secs / 60)}m ${secs % 60}s`
}

function SyncProgress({ status, startedAt, large, errorMsg }) {
  const [pct, setPct]         = useState(status === 'pending' ? 12 : 20)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (status === 'success' || status === 'error') return
    const t0 = startedAt ?? Date.now()
    const interval = setInterval(() => {
      const ms = Date.now() - t0
      setElapsed(ms)
      // logarithmic fill: fast early, tapers toward 90% over ~3 min
      const s = ms / 1000
      setPct(status === 'pending' ? 12 : Math.min(90, 15 + 75 * (1 - Math.exp(-s / 45))))
    }, 1000)
    return () => clearInterval(interval)
  }, [status, startedAt])

  const isSuccess = status === 'success'
  const isError   = status === 'error'
  const fillPct   = isSuccess ? 100 : isError ? 100 : pct

  const label = isSuccess ? 'Sync complete'
    : isError ? 'Sync failed'
    : status === 'pending' ? 'Starting...'
    : `Syncing · ${formatElapsed(elapsed)}`

  return (
    <div className={`${styles.syncProgress} ${large ? styles.syncProgressLg : ''}`}>
      <div className={styles.syncProgressTrack}>
        <div
          className={`${styles.syncProgressFill}${isSuccess ? ` ${styles.syncProgressFillSuccess}` : isError ? ` ${styles.syncProgressFillError}` : ''}`}
          style={{ width: `${fillPct}%` }}
        />
      </div>
      <span className={`${styles.syncProgressLabel}${isSuccess ? ` ${styles.syncProgressLabelSuccess}` : isError ? ` ${styles.syncProgressLabelError}` : ''}`}>
        {label}
      </span>
      {isError && errorMsg && (
        <span className={styles.syncProgressErrorMsg}>{errorMsg}</span>
      )}
    </div>
  )
}

function formatHistoryTime(ts) {
  const now = Date.now()
  const diffMs = now - ts
  const mins = Math.floor(diffMs / 60000)

  if (mins < 1)   return 'Just now'
  if (mins < 60)  return `${mins} min ago`

  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs} hr ago`

  const d = new Date(ts)
  const yesterday = new Date(now - 86400000)
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth()    === yesterday.getMonth()    &&
    d.getDate()     === yesterday.getDate()
  ) {
    return `Yesterday ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
  }

  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

// ── ConnectorDetail ──────────────────────────────────────────────────────────

function ConnectorDetail({ connector, onBack, onSync, syncing, syncStatus, syncStartedAt, syncErrorMsg, onDisconnect, openChat, apiFetch, onScheduleChange }) {
  const meta   = CATALOG_META[connector.provider] ?? { icon: '⌁', category: 'Data Source', name: connector.provider }
  const schema = getSchemaForConnector(connector.provider)

  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [history, setHistory]               = useState([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [tables, setTables]                 = useState(null) // null = loading
  const [tablesLoading, setTablesLoading]   = useState(true)
  const [scheduleId, setScheduleId] = useState(
    CRON_TO_ID[connector.sync_schedule] ?? 'every_6h'
  )

  const fetchHistory = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/connectors/${connector.id}/sync-history`)
      const data = await res.json()
      setHistory(data.jobs || [])
    } catch {
      setHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }, [connector.id, apiFetch])

  const fetchTables = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/connectors/${connector.id}/tables`)
      if (res.ok) {
        const data = await res.json()
        setTables(data.tables || [])
      } else {
        setTables([])
      }
    } catch {
      setTables([])
    } finally {
      setTablesLoading(false)
    }
  }, [connector.id, apiFetch])

  useEffect(() => {
    fetchHistory()
    fetchTables()
  }, [fetchHistory, fetchTables])

  // While a sync is running, poll history every 5s so the table updates live
  useEffect(() => {
    if (!syncing) return
    const timer = setInterval(fetchHistory, 5000)
    return () => clearInterval(timer)
  }, [syncing, fetchHistory])

  // Refresh history + tables once when sync flips from running → done
  const prevSyncing = useRef(syncing)
  useEffect(() => {
    if (prevSyncing.current && !syncing) { fetchHistory(); fetchTables() }
    prevSyncing.current = syncing
  }, [syncing, fetchHistory, fetchTables])

  const successJobs = history.filter(j => j.status === 'success')
  const totalRows = connector.rows
    ? Number(connector.rows).toLocaleString()
    : successJobs.length > 0
      ? successJobs.reduce((s, j) => s + (j.rowsLoaded || 0), 0).toLocaleString()
      : tables?.length
        ? tables.reduce((s, t) => s + (t.rows || 0), 0).toLocaleString()
        : historyLoading ? '…' : '—'

  const lastSyncDisplay = connector.lastSync
    ?? (successJobs.length > 0
      ? (successJobs[0].completedAt ?? successJobs[0].createdAt)
      : null)

  return (
    <div className={styles.detailRoot}>

      {/* Back + header */}
      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderLeft}>
          <button className={styles.backBtn} onClick={onBack}>← Back</button>
          <div className={styles.detailTitleRow}>
            <BrandLogo id={connector.provider} fallback={meta.icon} size={40} className={styles.detailIcon} />
            <div>
              <div className={styles.detailName}>{connector.name ?? meta.name}</div>
              <span className={styles.detailCategory}>{meta.category}</span>
            </div>
            <StatusBadge status={connector.status} />
          </div>
        </div>

        <div className={styles.detailActions}>
          {syncing ? (
            <SyncProgress
              status={syncStatus ?? 'pending'}
              startedAt={syncStartedAt}
              errorMsg={syncErrorMsg}
              large
            />
          ) : (
            <div className={styles.syncGroup}>
              <button
                className={`${styles.syncBtn} ${styles.syncBtnLg}`}
                onClick={() => onSync(connector.id)}
              >
                ↻ Sync Now
              </button>
              <button
                className={styles.fullRefreshBtn}
                onClick={() => onSync(connector.id, 'full_refresh')}
                title="Drop and reload all data from scratch"
              >
                Full Refresh
              </button>
            </div>
          )}

          {confirmDisconnect ? (
            <div className={styles.disconnectConfirm}>
              <span className={styles.disconnectWarning}>Are you sure?</span>
              <button
                className={styles.disconnectConfirmBtn}
                onClick={() => onDisconnect(connector.id)}
              >
                Disconnect
              </button>
              <button
                className={styles.disconnectCancelBtn}
                onClick={() => setConfirmDisconnect(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className={styles.disconnectBtn}
              onClick={() => setConfirmDisconnect(true)}
            >
              Disconnect
            </button>
          )}
        </div>
      </div>

      {/* Stats strip */}
      <div className={styles.statsStrip}>
        <div className={styles.statBox}>
          <span className={styles.statBoxVal}>{totalRows}</span>
          <span className={styles.statBoxLabel}>Total Rows Synced</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statBoxVal}>{formatLastSync(lastSyncDisplay)}</span>
          <span className={styles.statBoxLabel}>Last Sync</span>
        </div>
        <div className={`${styles.statBox} ${styles.statBoxLast}`}>
          <span className={styles.statBoxVal}>{tables == null ? '…' : tables.length}</span>
          <span className={styles.statBoxLabel}>Tables</span>
        </div>
      </div>

      {/* Sync schedule */}
      <SyncScheduleBar
        connectorId={connector.id}
        schedule={scheduleId}
        lastSync={connector.lastSync}
        apiFetch={apiFetch}
        onSaved={(id, cron) => { setScheduleId(id); onScheduleChange?.(connector.id, cron) }}
      />

      {/* Sync History */}
      <section className={styles.detailSection}>
        <div className={styles.sectionLabel}>Sync History</div>
        {historyLoading && !syncing ? (
          <div className={styles.historyEmpty}>Loading…</div>
        ) : !syncing && history.length === 0 ? (
          <div className={styles.historyEmpty}>No sync runs yet. Click Sync Now to start the first sync.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.historyTable}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Rows loaded</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {syncing && (
                  <tr key="__live__" className={styles.historyRowLive}>
                    <td className={styles.monoCell}>Now</td>
                    <td style={{ minWidth: 180 }}>
                      <SyncProgress
                        status={syncStatus ?? 'pending'}
                        startedAt={syncStartedAt}
                        errorMsg={syncErrorMsg}
                      />
                    </td>
                    <td className={styles.monoCell}>—</td>
                    <td className={styles.monoCell}>—</td>
                  </tr>
                )}
                {history.map(job => (
                  <tr key={job.id}>
                    <td className={styles.monoCell}>{formatHistoryTime(new Date(job.createdAt).getTime())}</td>
                    <td>
                      <span className={`${styles.historyPill} ${styles[`historyPill_${job.status}`]}`}>
                        {job.status}
                      </span>
                      {job.status === 'error' && job.errorMessage && (
                        <div className={styles.historyErrorMsg}>{sanitizeErrorMessage(job.errorMessage)}</div>
                      )}
                    </td>
                    <td className={styles.monoCell}>
                      {job.rowsLoaded > 0 ? job.rowsLoaded.toLocaleString() : '—'}
                    </td>
                    <td className={styles.monoCell}>{computeDuration(job.startedAt, job.completedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Source Manifest */}
      <section className={styles.detailSection}>
        <div className={styles.sectionLabel}>
          <span className={styles.layerTag}>SOURCE</span>
          Data Manifest
        </div>
        <p className={styles.manifestDesc}>{schema.description}</p>

        {tablesLoading ? (
          <div className={styles.historyEmpty}>Loading tables…</div>
        ) : tables && tables.length > 0 ? (
          <>
            <FieldPolicySection
              connector={connector}
              tables={tables}
              apiFetch={apiFetch}
            />
            <div className={styles.manifestList}>
              {tables.map((t) => (
                <ManifestRow
                  key={t.name}
                  table={t}
                  provider={connector.provider}
                  connectorName={connector.name ?? meta.name}
                  openChat={openChat}
                />
              ))}
            </div>
          </>
        ) : (
          <div className={styles.historyEmpty}>
            No tables synced yet. Click <strong>Sync Now</strong> to load data from {connector.name ?? meta.name}.
          </div>
        )}

        {/* AI CTA panel */}
        <div className={styles.aiPanel}>
          <div className={styles.aiPanelHeader}>
            <span className={styles.aiPanelIcon}>◈</span>
            <span className={styles.aiPanelTitle}>Explore with AI</span>
          </div>
          <div className={styles.aiPrompts}>
            {schema.aiPrompts.map((prompt) => (
              <button
                key={prompt}
                className={styles.aiPromptBtn}
                onClick={() => openChat(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      </section>

    </div>
  )
}

// ── FieldPolicySection ────────────────────────────────────────────────────────

function FieldPolicySection({ connector, tables, apiFetch }) {
  const flagged = _detectPiiColumns(tables)
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Build initial checked state from existing excluded_fields on the connector
  const existingExcludes = connector.excluded_fields || {}
  const [checked, setChecked] = useState(() => {
    const init = {}
    for (const { table, column } of flagged) {
      init[`${table}::${column}`] = !!(existingExcludes[table] || []).includes(column)
    }
    return init
  })

  if (flagged.length === 0) return null

  function toggle(key) {
    setChecked(prev => ({ ...prev, [key]: !prev[key] }))
    setSaved(false)
  }

  async function save() {
    setSaving(true)
    setError(null)
    // Rebuild excluded_fields from checked state
    const policy = {}
    for (const [key, isExcluded] of Object.entries(checked)) {
      if (!isExcluded) continue
      const [table, column] = key.split('::')
      ;(policy[table] = policy[table] || []).push(column)
    }
    try {
      const res = await apiFetch(`/api/connectors/${connector.id}/field-policy`, {
        method: 'PATCH',
        body: JSON.stringify({ excluded_fields: policy }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      setSaved(true)
    } catch (e) {
      setError(`Failed to save: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const anyChecked = Object.values(checked).some(Boolean)

  return (
    <section className={styles.fieldPolicySection}>
      <div className={styles.fieldPolicyHeader}>
        <span className={styles.fieldPolicyIcon}>⚠</span>
        <div>
          <div className={styles.fieldPolicyTitle}>Sensitive fields detected</div>
          <div className={styles.fieldPolicySubtitle}>
            {flagged.length} column{flagged.length !== 1 ? 's' : ''} match PII or PCI patterns.
            Check the ones to exclude — they will be dropped before data reaches the lake.
          </div>
        </div>
      </div>

      <table className={styles.fieldPolicyTable}>
        <thead>
          <tr>
            <th className={styles.fieldPolicyExcludeCol}>Exclude</th>
            <th>Table</th>
            <th>Column</th>
            <th>Matched pattern</th>
          </tr>
        </thead>
        <tbody>
          {flagged.map(({ table, column, signal }) => {
            const key = `${table}::${column}`
            return (
              <tr key={key} className={checked[key] ? styles.fieldPolicyRowExcluded : ''}>
                <td className={styles.fieldPolicyExcludeCol}>
                  <input
                    type="checkbox"
                    checked={!!checked[key]}
                    onChange={() => toggle(key)}
                    aria-label={`Exclude ${column}`}
                  />
                </td>
                <td className={styles.monoCell}>{table}</td>
                <td className={styles.monoCell}>{column}</td>
                <td className={styles.fieldPolicySignal}>{signal}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className={styles.fieldPolicyActions}>
        <button
          className={styles.fieldPolicySaveBtn}
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save policy'}
        </button>
        {saved && (
          <span className={styles.fieldPolicySavedMsg}>
            ✓ Saved — re-sync to apply
          </span>
        )}
        {anyChecked && !saved && (
          <span className={styles.fieldPolicyResyncNote}>
            Re-sync after saving to drop excluded fields from the lake.
          </span>
        )}
        {error && <span className={styles.fieldPolicyError}>{error}</span>}
      </div>
    </section>
  )
}

function ManifestRow({ table, provider, connectorName, openChat }) {
  const [expanded, setExpanded] = useState(false)

  const explorePrompt =
    `Show me the ${table.name} data from ${connectorName}. What are the key fields and can you show me a sample?`

  const providerKey = provider.replace(/-/g, '_')
  const modelName   = `stg_${providerKey}__${table.name.replace(/[^a-z0-9_]/g, '_')}`

  return (
    <div className={styles.manifestRow}>
      <div className={styles.manifestRowTop}>
        <div className={styles.manifestRowLeft}>
          <button
            className={styles.manifestExpandBtn}
            onClick={() => setExpanded(e => !e)}
            title={expanded ? 'Collapse' : 'Show columns'}
          >
            {expanded ? '▾' : '▸'}
          </button>
          <span className={styles.manifestTableName}>{table.name}</span>
          {table.rows != null && (
            <span className={styles.manifestRows}>{table.rows.toLocaleString()} rows</span>
          )}
          {table.lastSynced && (
            <span className={styles.manifestSynced}>{table.lastSynced}</span>
          )}
        </div>
        <div className={styles.manifestRowActions}>
          <button
            className={styles.exploreBtn}
            onClick={() => openChat(explorePrompt)}
          >
            Explore →
          </button>
        </div>
      </div>

      {expanded && (
        <div className={styles.manifestColumns}>
          {table.columns.map((col) => (
            <span key={col} className={styles.colChip}>{col}</span>
          ))}
          <span className={styles.colChipModel}>→ {modelName}</span>
        </div>
      )}
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className={styles.skeleton}>
      <div className={`${styles.skeletonLine} ${styles.skeletonIcon}`} />
      <div className={`${styles.skeletonLine} ${styles.skeletonTitle}`} />
      <div className={`${styles.skeletonLine} ${styles.skeletonSub}`} />
    </div>
  )
}

function StatusBadge({ status }) {
  const map = {
    connected: { label: 'Connected', cls: styles.badgeConnected },
    syncing:   { label: 'Syncing',   cls: styles.badgeSyncing   },
    error:     { label: 'Error',     cls: styles.badgeError     },
    pending:   { label: 'Pending',   cls: styles.badgeSyncing   },
  }
  const { label, cls } = map[status] ?? { label: status, cls: '' }
  return <span className={`${styles.badge} ${cls}`}>{label}</span>
}

function ActiveCard({ connector, onSync, syncing, syncStatus, syncStartedAt, syncErrorMsg, onClick, onReauth, reauthing, reauthErrorMsg }) {
  const meta  = CATALOG_META[connector.provider] ?? { icon: '⌁', category: 'Data Source' }
  const isErr = connector.status === 'error'
  const needsReauth = connector.nango_auth_status === 'pending_auth'

  return (
    <div
      className={`${styles.card} ${styles[connector.status] ?? ''} ${isErr ? styles.cardError : ''}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardTop}>
        <BrandLogo id={connector.provider} fallback={meta.icon} size={28} className={styles.cardIcon} />
        <StatusBadge status={connector.status} />
      </div>
      <div className={styles.cardName}>{connector.name ?? meta.name}</div>
      <div className={styles.cardCategory}>{meta.category}</div>
      {isErr && connector.errorMessage && (
        <div className={styles.errorMsg}>{connector.errorMessage}</div>
      )}
      {needsReauth && (
        <div className={styles.reAuthRow} onClick={e => e.stopPropagation()}>
          <span className={styles.reAuthBanner}>⚠ Authorization required</span>
          <button
            className={styles.reAuthBtn}
            disabled={reauthing}
            onClick={() => onReauth(connector.id, connector.provider)}
          >
            {reauthing ? 'Authorizing…' : 'Re-authorize →'}
          </button>
          {reauthErrorMsg && <div className={styles.errorMsg}>{reauthErrorMsg}</div>}
        </div>
      )}
      {syncing && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 'var(--sp-2)' }}>
          <SyncProgress status={syncStatus ?? 'pending'} startedAt={syncStartedAt} errorMsg={syncErrorMsg} />
        </div>
      )}
      <div className={styles.cardFooter}>
        <span className={styles.cardSync}>⌁ {formatLastSync(connector.lastSync)}</span>
        {formatRows(connector.rows) && (
          <span className={styles.cardRows}>{formatRows(connector.rows)} rows</span>
        )}
        <button
          className={styles.syncBtn}
          onClick={(e) => { e.stopPropagation(); onSync(connector.id) }}
          disabled={syncing}
          title="Sync now"
          style={syncing ? { opacity: 0.25 } : undefined}
        >
          ↻
        </button>
      </div>
    </div>
  )
}

// ── ConnectorsPage ────────────────────────────────────────────────────────────

export default function ConnectorsPage() {
  const { openChat } = useOutletContext()
  const { apiFetch } = useAuth()

  const [connectors, setConnectors] = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)
  const [syncing, setSyncing]           = useState({})
  const [pendingJobs, setPendingJobs]   = useState({}) // { connectorId: syncJobId }
  const [syncStatuses, setSyncStatuses]   = useState({}) // { connectorId: 'pending'|'running'|'success'|'error' }
  const [syncStartTimes, setSyncStartTimes] = useState({}) // { connectorId: ms timestamp }
  const [syncErrorMsgs, setSyncErrorMsgs] = useState({}) // { connectorId: string }
  const [reauthing, setReauthing]       = useState({}) // { connectorId: bool }
  const [reauthErrors, setReauthErrors] = useState({}) // { connectorId: string }
  const [reauthCredModal, setReauthCredModal] = useState(null) // { connectorId, provider, nangoId } | null
  const [selected, setSelected]     = useState(null)
  const [enabledOverrides, setEnabledOverrides] = useState({})

  const [catalogOpen, setCatalogOpen]     = useState(false)
  const [connectTarget, setConnectTarget] = useState(null)

  useEffect(() => {
    apiFetch('/api/settings/connectors')
      .then(r => r.ok ? r.json() : { overrides: {} })
      .then(d => setEnabledOverrides(d.overrides ?? {}))
      .catch(() => {})
  }, [apiFetch])

  const fetchConnectors = useCallback(async () => {
    try {
      const res = await apiFetch('/api/connectors')
      if (!res.ok) throw new Error(`API error ${res.status}`)
      const data = await res.json()
      setConnectors(data.connectors ?? [])
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [apiFetch])

  useEffect(() => { fetchConnectors() }, [fetchConnectors])

  const _clearSyncState = useCallback((id, delay = 0) => {
    setTimeout(() => {
      setSyncing(s => { const n = { ...s }; delete n[id]; return n })
      setTimeout(() => {
        setSyncStatuses(s => { const n = { ...s }; delete n[id]; return n })
        setSyncStartTimes(s => { const n = { ...s }; delete n[id]; return n })
        setSyncErrorMsgs(s => { const n = { ...s }; delete n[id]; return n })
      }, 1500)
    }, delay)
  }, [])

  const handleSync = async (id, syncType = 'incremental') => {
    setSyncing(s => ({ ...s, [id]: true }))
    setSyncStatuses(s => ({ ...s, [id]: 'pending' }))
    setSyncStartTimes(s => ({ ...s, [id]: Date.now() }))
    setSyncErrorMsgs(s => { const n = { ...s }; delete n[id]; return n })
    try {
      const res = await apiFetch(`/api/connectors/${id}/sync`, {
        method: 'POST',
        body: JSON.stringify({ sync_type: syncType }),
      })
      if (res.ok) {
        const data = await res.json()
        if (data.syncJobId) {
          setPendingJobs(prev => ({ ...prev, [id]: data.syncJobId }))
          return  // polling effect will clear syncing when job finishes
        }
      } else {
        const data = await res.json().catch(() => ({}))
        setSyncStatuses(s => ({ ...s, [id]: 'error' }))
        setSyncErrorMsgs(s => ({ ...s, [id]: data.error || `Server error (${res.status})` }))
        _clearSyncState(id, 4000)
        return
      }
    } catch (e) {
      setSyncStatuses(s => ({ ...s, [id]: 'error' }))
      setSyncErrorMsgs(s => ({ ...s, [id]: e.message || 'Network error' }))
      _clearSyncState(id, 4000)
      return
    }
    // Got a response but no syncJobId — shouldn't happen, clear quietly
    _clearSyncState(id, 2000)
  }

  // Completes a reauth once we have everything nango.auth() needs — called either directly
  // (standard OAuth providers) or from the credential re-entry modal's submit handler
  // (oauthOverride providers like NetSuite, which supply `credentials` here).
  const _performReauth = async (connectorId, nangoId, credentials) => {
    setReauthing(r => ({ ...r, [connectorId]: true }))
    setReauthErrors(e => { const n = { ...e }; delete n[connectorId]; return n })
    try {
      const sessionRes = await apiFetch(`/api/connectors/${connectorId}/nango-reconnect-session`, {
        method: 'POST',
        body: JSON.stringify({ integration_id: nangoId }),
      })
      if (!sessionRes.ok) throw new Error('Failed to create reconnect session')
      const { token } = await sessionRes.json()
      const nango = new Nango({ connectSessionToken: token })
      const authOpts = credentials && Object.keys(credentials).length ? { credentials } : undefined
      await nango.auth(nangoId, authOpts)
      await apiFetch(`/api/connectors/${connectorId}/reauth`, { method: 'POST', body: JSON.stringify({}) })
      fetchConnectors()
    } catch (err) {
      console.error('Reauth error:', err)
      setReauthErrors(e => ({ ...e, [connectorId]: err.message || 'Reauthorization failed' }))
      setTimeout(() => setReauthErrors(e => { const n = { ...e }; delete n[connectorId]; return n }), 6000)
    } finally {
      setReauthing(r => { const n = { ...r }; delete n[connectorId]; return n })
    }
  }

  const handleReauth = (connectorId, provider) => {
    const catalogEntry = CATALOG_BY_ID[provider]
    const nangoId = catalogEntry?.nangoId || provider
    const overrideFields = (catalogEntry?.connectionParams ?? []).filter(p => p.oauthOverride)

    if (overrideFields.length) {
      // Customer-brought-their-own-OAuth-app providers (NetSuite today) never have their
      // Client ID/Secret persisted anywhere on our side, so reauth can't proceed silently —
      // collect them again first.
      setReauthCredModal({ connectorId, provider: catalogEntry, nangoId, overrideFields })
      return
    }
    _performReauth(connectorId, nangoId, null)
  }

  // Poll sync-history for each pending job until it finishes
  useEffect(() => {
    if (Object.keys(pendingJobs).length === 0) return
    const timer = setInterval(async () => {
      for (const [connectorId, syncJobId] of Object.entries(pendingJobs)) {
        try {
          const res = await apiFetch(`/api/connectors/${connectorId}/sync-history`)
          const data = await res.json()
          const job = (data.jobs ?? []).find(j => j.id === syncJobId)
          if (job) {
            setSyncStatuses(prev => ({ ...prev, [connectorId]: job.status }))
            if (job.errorMessage) {
              setSyncErrorMsgs(prev => ({ ...prev, [connectorId]: job.errorMessage }))
            }
          }
          if (job && job.status !== 'pending' && job.status !== 'running') {
            // Stop polling immediately
            setPendingJobs(prev => { const n = { ...prev }; delete n[connectorId]; return n })
            fetchConnectors()
            // If sync succeeded, trigger the definition agent (fire-and-forget)
            if (job.status === 'success') {
              apiFetch(`/api/connectors/${connectorId}/document`, { method: 'POST' }).catch(() => {})
            }
            // Show completion state (error stays visible longer) then clear
            _clearSyncState(connectorId, job.status === 'error' ? 5000 : 2000)
          }
        } catch {
          // transient fetch error — retry on the next poll tick
        }
      }
    }, 5000)
    return () => clearInterval(timer)
  }, [pendingJobs, apiFetch, fetchConnectors, _clearSyncState])

  const handleConnect = (provider) => {
    setCatalogOpen(false)
    setConnectTarget(provider)
  }

  const handleConnected = async (connectionId, provider, name, connectionParams = {}, dbCredentials = null) => {
    try {
      await apiFetch('/api/connectors', {
        method: 'POST',
        body: JSON.stringify({ connectionId, provider, name, connectionParams, dbCredentials }),
      })
    } catch (err) {
      console.error('Failed to save connection:', err)
    }
    setConnectTarget(null)
    fetchConnectors()
  }

  const handleDisconnect = async (id) => {
    try {
      await apiFetch(`/api/connectors/${id}`, {
        method: 'DELETE',
      })
    } catch (err) {
      console.error('Disconnect error:', err)
    }
    setConnectors(cs => cs.filter(c => c.id !== id))
    setSelected(null)
  }

  const activeCounts = connectors.reduce((acc, c) => { acc[c.provider] = (acc[c.provider] ?? 0) + 1; return acc }, {})
  const activeCount  = connectors.filter(c => c.status === 'connected').length
  const existingSlugs = new Set(connectors.map(c => c.slug).filter(Boolean))

  // Keep selected in sync if connectors list changes (e.g. after a sync refresh)
  const selectedConnector = selected
    ? (connectors.find(c => c.id === selected.id) ?? selected)
    : null

  if (selectedConnector) {
    return (
      <div className={`${styles.root} anim-fade`}>
        <ConnectorDetail
          connector={selectedConnector}
          onBack={() => setSelected(null)}
          onSync={handleSync}
          syncing={!!syncing[selectedConnector.id]}
          syncStatus={syncStatuses[selectedConnector.id]}
          syncStartedAt={syncStartTimes[selectedConnector.id]}
          syncErrorMsg={syncErrorMsgs[selectedConnector.id]}
          onDisconnect={handleDisconnect}
          openChat={openChat}
          apiFetch={apiFetch}
        />
      </div>
    )
  }

  return (
    <div className={`${styles.root} anim-fade`}>

      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Connectors</h1>
          <p className={styles.sub}>Manage data source integrations into your Structura Data lake</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)' }}>
          {!loading && (
            <div className={styles.stats}>
              <div className={styles.stat}>
                <span className={styles.statVal}>{activeCount}</span>
                <span className={styles.statLabel}>Active</span>
              </div>
            </div>
          )}
          <button className={styles.browseBtn} onClick={() => setCatalogOpen(true)}>
            + Add Connector
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className={styles.errorBanner}>
          ⚠ Could not load connectors — {error}
          <button className={styles.retryBtn} onClick={fetchConnectors}>Retry</button>
        </div>
      )}

      {/* Active connectors */}
      <section className={styles.section}>
        <div className={styles.sectionLabel}>
          Active Connectors
        </div>

        {loading ? (
          <div className={styles.grid}>
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
        ) : connectors.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>⌁</div>
            <p className={styles.emptyText}>No data sources connected yet.</p>
            <button className={styles.emptyBtn} onClick={() => setCatalogOpen(true)}>
              Browse connectors →
            </button>
          </div>
        ) : (
          <div className={styles.grid}>
            {connectors.map(c => (
              <ActiveCard
                key={c.id}
                connector={c}
                onSync={handleSync}
                syncing={!!syncing[c.id]}
                syncStatus={syncStatuses[c.id]}
                syncStartedAt={syncStartTimes[c.id]}
                syncErrorMsg={syncErrorMsgs[c.id]}
                onClick={() => setSelected(c)}
                onReauth={handleReauth}
                reauthing={!!reauthing[c.id]}
                reauthErrorMsg={reauthErrors[c.id]}
              />
            ))}
          </div>
        )}
      </section>

      {/* Modals */}
      <ConnectorCatalogModal
        isOpen={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        activeCounts={activeCounts}
        onConnect={handleConnect}
        enabledOverrides={enabledOverrides}
      />

      <ConnectionModal
        isOpen={!!connectTarget}
        provider={connectTarget}
        onClose={() => setConnectTarget(null)}
        onChatFallback={(prompt) => { setConnectTarget(null); openChat(prompt) }}
        onConnected={handleConnected}
        existingSlugs={existingSlugs}
      />

      <ReauthCredentialsModal
        isOpen={!!reauthCredModal}
        provider={reauthCredModal?.provider}
        overrideFields={reauthCredModal?.overrideFields ?? []}
        onClose={() => setReauthCredModal(null)}
        onSubmit={(credentials) => {
          const { connectorId, nangoId } = reauthCredModal
          setReauthCredModal(null)
          _performReauth(connectorId, nangoId, credentials)
        }}
      />

    </div>
  )
}
